import React, { useState, useEffect } from 'react'
import {
  X,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react'

const ReceiptEditor = ({ receipt, patient, onClose, onSave, isPreview = false, previewActionLabel = 'Confirm & Generate' }) => {
  const [formData, setFormData] = useState({
    receipt_date: '',
    receipt_time: '',
    items: [],
    narration: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (receipt) {
      setFormData({
        receipt_date: receipt.receipt_date,
        receipt_time: receipt.receipt_time,
        items: receipt.items || [],
        narration: receipt.narration || ''
      })
    }
  }, [receipt])

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = String(date.getFullYear()).slice(-2)
    return `${day}-${month}-${year}`
  }

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        sl_no: prev.items.length + 1,
        particular: '',
        amount: ''
      }]
    }))
  }

  const removeItem = (index) => {
    if (formData.items.length === 1) return

    const newItems = formData.items.filter((_, i) => i !== index)
    // Renumber items
    newItems.forEach((item, idx) => {
      item.sl_no = idx + 1
    })

    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items]
    newItems[index][field] = value
    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0
      return sum + amount
    }, 0)
  }

  const amountToWordsIndian = (value) => {
    let amount = Math.round(Number(value) || 0)
    if (amount === 0) return 'ZERO RUPEES ONLY'

    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
      'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN']
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY']

    const belowThousand = (number) => {
      const words = []
      if (number >= 100) {
        words.push(ones[Math.floor(number / 100)])
        words.push('HUNDRED')
        number %= 100
      }
      if (number >= 20) {
        words.push(tens[Math.floor(number / 10)])
        number %= 10
      }
      if (number) words.push(ones[number])
      return words.join(' ')
    }

    const parts = []
    const crore = Math.floor(amount / 10000000)
    amount %= 10000000
    const lakh = Math.floor(amount / 100000)
    amount %= 100000
    const thousand = Math.floor(amount / 1000)
    amount %= 1000

    if (crore) parts.push(`${belowThousand(crore)} CRORE`)
    if (lakh) parts.push(`${belowThousand(lakh)} LAKH`)
    if (thousand) parts.push(`${belowThousand(thousand)} THOUSAND`)
    if (amount) parts.push(belowThousand(amount))

    return `${parts.join(' ')} RUPEES ONLY`
  }

  const parseIPFinalBillDetails = (narration) => {
    if (!narration || !narration.toUpperCase().includes('IP FINAL BILL')) return null

    const details = {}
    narration.split('|').forEach((part) => {
      const [key, ...valueParts] = part.split(':')
      if (!key || valueParts.length === 0) return
      details[key.trim().toLowerCase()] = valueParts.join(':').trim()
    })

    return {
      receiptNo: receipt?.receipt_no || '',
      receiptDate: formData.receipt_date || '',
      receiptTime: formData.receipt_time || '',
      uhid: patient?.uhid_no || patient?.uhid || '',
      patientName: patient?.full_name || patient?.name || '',
      admNo: details['adm no'] || '',
      wardNo: details['ward no'] || '',
      doa: details.doa || '',
      dod: details.dod || '',
      consultant: details.consultant || '',
      department: details.department || '',
      referredBy: details['referred by'] || '',
      ageSex: details['age/sex'] || '',
      remarks: details.remarks || ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Validate
    const hasEmptyItems = formData.items.some(item => !item.particular || !item.amount)
    if (hasEmptyItems) {
      setError('All items must have particulars and amount')
      return
    }

    try {
      setLoading(true)

      // Prepare data
      const updateData = {
        receipt_date: formData.receipt_date,
        receipt_time: formData.receipt_time,
        items: formData.items.map(item => ({
          sl_no: item.sl_no,
          particular: item.particular,
          amount: parseFloat(item.amount)
        })),
        narration: formData.narration
      }

      // In preview mode: pass null for receiptId (new receipt)
      // In edit mode: pass receipt.receipt_id
      await onSave(isPreview ? null : receipt.receipt_id, updateData)

      const successMsg = isPreview ? 'Receipt generated successfully!' : 'Receipt updated successfully!'
      setSuccess(successMsg)

      // Close after a short delay
      setTimeout(() => {
        onClose()
      }, 1500)

    } catch (err) {
      console.error('Receipt error:', err)
      const errorMessage = err.response?.data?.error || err.message || (isPreview ? 'Failed to generate receipt' : 'Failed to update receipt')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const totalAmount = calculateTotal()
  const ipFinalBillDetails = parseIPFinalBillDetails(formData.narration)
  const narrationValue = ipFinalBillDetails?.remarks || (ipFinalBillDetails ? 'IP FINAL BILL' : formData.narration)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center py-8 px-4">
        {/* Floating Toolbar */}
        <div className="fixed top-4 right-4 z-60 flex gap-2">
        {error && (
          <div className="bg-red-50 border border-red-300 rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
            <AlertCircle className="text-red-600" size={18} />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-300 rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
            <CheckCircle className="text-green-600" size={18} />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-lg disabled:opacity-50 ${
            isPreview 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {isPreview ? 'Generating...' : 'Saving...'}
            </>
          ) : (
            <>
              <Save size={16} />
              {isPreview ? previewActionLabel : 'Save'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors shadow-lg"
        >
          <X size={20} />
        </button>
      </div>

        {/* Receipt Document - Looks like PDF */}
        <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl" style={{fontFamily: 'Arial, sans-serif', fontSize: '11px'}}>
          <div className="p-8">
          {/* Header Section - Matches PDF exactly */}
          {ipFinalBillDetails ? (
            null
          ) : (
            <div className="text-center border-b-2 border-black pb-3 mb-4">
              <div className="text-sm font-bold mb-1">EPIONE PAIN CARE PRIVATE LIMITED</div>
              <div className="text-[9px] leading-tight mb-2 text-gray-700">
                {patient?.center_address || '2nd Pillar No. 1630, Apurupa Towers, 8-2-293/82/A 245, Road No. 36, Jawahar Colony, Jubilee Hills, Hyderabad, Telangana'}
              </div>
              <div className="flex justify-between text-[9px] leading-tight">
                <div className="text-left w-1/2">
                  Ph: {patient?.center_phone || '9876543210'}<br/>
                  E-mail: {patient?.center_email || 'contact@epionepaincare.com'}<br/>
                  Website: {patient?.center_website || 'epionepainandspine.com'}
                </div>
                <div className="text-right w-1/2">
                  GST No: {patient?.center_gst || '36AAFCE6957Q1ZR'}<br/>
                  PAN No: {patient?.center_pan || 'AAFCE6957Q1'}<br/>
                  Emergency:
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          {!ipFinalBillDetails && (
            <div className="text-center text-xs font-bold my-3 underline">
              PATIENT RECEIPT VOUCHER
            </div>
          )}
          {isPreview && (
            <div className="text-center text-[9px] text-blue-600 font-semibold mb-3 bg-blue-50 py-1 rounded">
              Preview - Review and make changes if needed, then confirm to generate
            </div>
          )}

          {/* Receipt Information - PDF Style */}
          {!ipFinalBillDetails && (
          <div className="text-[10px] mb-3">
            <div className="flex mb-0.5">
              <div className="w-[20%]">Receipt No</div>
              <div className="w-[3%] text-center">:</div>
              <div className="w-[77%] font-semibold">{isPreview ? 'NEW (Will be generated)' : receipt?.receipt_no}</div>
            </div>
            <div className="flex mb-0.5">
              <div className="w-[20%]">Receipt Date</div>
              <div className="w-[3%] text-center">:</div>
              <div className="w-[77%]">
                <input
                  type="date"
                  value={formData.receipt_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, receipt_date: e.target.value }))}
                  className="border-none bg-transparent focus:bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-blue-400 px-1 rounded"
                  required
                />
                <span className="ml-2 text-gray-500">({formatDate(formData.receipt_date)})</span>
              </div>
            </div>
            <div className="flex mb-0.5">
              <div className="w-[20%]">Receipt Time</div>
              <div className="w-[3%] text-center">:</div>
              <div className="w-[77%]">
                <input
                  type="time"
                  value={formData.receipt_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, receipt_time: e.target.value }))}
                  className="border-none bg-transparent focus:bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-blue-400 px-1 rounded"
                  required
                />
              </div>
            </div>
          </div>
          )}

          {ipFinalBillDetails && (
            <>
              <div className="text-center text-sm font-bold tracking-wide underline mb-2" style={{ marginTop: '76.2mm' }}>IP FINAL BILL</div>
              <div className="border border-black p-2 mb-3 text-[10px]">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <div><strong>Receipt No:</strong> {ipFinalBillDetails.receiptNo}</div>
                  <div><strong>Receipt Date:</strong> {ipFinalBillDetails.receiptDate}</div>
                  <div><strong>Receipt Time:</strong> {ipFinalBillDetails.receiptTime}</div>
                  <div><strong>UHID:</strong> {ipFinalBillDetails.uhid}</div>
                  <div><strong>Patient Name:</strong> {ipFinalBillDetails.patientName}</div>
                  <div><strong>Adm No:</strong> {ipFinalBillDetails.admNo}</div>
                  <div><strong>Ward No:</strong> {ipFinalBillDetails.wardNo}</div>
                  <div><strong>DOA:</strong> {ipFinalBillDetails.doa}</div>
                  <div><strong>DOD:</strong> {ipFinalBillDetails.dod}</div>
                  <div><strong>Consultant:</strong> {ipFinalBillDetails.consultant}</div>
                  <div><strong>Department:</strong> {ipFinalBillDetails.department}</div>
                  <div><strong>Referred By:</strong> {ipFinalBillDetails.referredBy}</div>
                  <div><strong>Age / Sex:</strong> {ipFinalBillDetails.ageSex}</div>
                </div>
              </div>
            </>
          )}

          {/* Items Table - Exact PDF Match */}
          <table className={`w-full text-[10px] ${ipFinalBillDetails ? 'mt-0 mb-3 border-separate border-spacing-0 border-l border-r border-t border-black' : 'my-3 border-collapse'}`}>
            <thead>
              <tr className={ipFinalBillDetails ? '' : 'border-t border-b border-black'}>
                <th className="py-1.5 px-1 text-center font-bold border-0" style={{width: '8%'}}>SI No</th>
                <th className="py-1.5 px-1 text-left font-bold border-0" style={{width: '60%'}}>PARTICULARS</th>
                <th className="py-1.5 px-1 text-right font-bold pr-2.5 border-0" style={{width: '32%'}}>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, index) => (
                <tr key={index} className="hover:bg-blue-50 group border-0">
                  <td className="py-1 px-1 text-center border-0">{item.sl_no}</td>
                  <td className="py-1 px-1 border-0">
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={item.particular}
                        onChange={(e) => updateItem(index, 'particular', e.target.value)}
                        className="w-full border-none bg-transparent focus:bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-blue-400 px-1 rounded"
                        placeholder="Enter particular"
                        required
                      />
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-red-600 hover:bg-red-100 rounded transition-all"
                          title="Remove"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-1 px-1 pr-2.5 text-right border-0">
                    <input
                      type="number"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => updateItem(index, 'amount', e.target.value)}
                      className="w-full border-none bg-transparent focus:bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-blue-400 px-1 rounded text-right"
                      placeholder="0.00"
                      required
                    />
                  </td>
                </tr>
              ))}
              {/* Add Item Button Row */}
              <tr className="hover:bg-gray-50">
                <td colSpan="3" className="py-1 px-1">
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-[9px]"
                  >
                    <Plus size={12} />
                    Add Item
                  </button>
                </td>
              </tr>
              {/* Total Row - PDF Style */}
              <tr className="border-t border-b border-black font-bold">
                {ipFinalBillDetails && (
                  <td colSpan="2" className="py-1.5 px-1 text-left whitespace-nowrap">
                    Inwords: {amountToWordsIndian(totalAmount)}
                  </td>
                )}
                {ipFinalBillDetails ? (
                  <td className="py-1.5 px-1 pr-2.5 text-right whitespace-nowrap">
                    <span className="mr-7">Total Amount:</span>
                    <span>{totalAmount.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                  </td>
                ) : (
                  <>
                    <td colSpan="2" className="py-1.5 px-1 text-right pr-2.5 whitespace-nowrap">Total Amount</td>
                    <td className="py-1.5 px-1 pr-2.5 text-right">{totalAmount.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                  </>
                )}
              </tr>
            </tbody>
          </table>

          {!ipFinalBillDetails && (
            <div className="border-t border-b border-black py-1 px-1 text-[10px] my-3">
              <strong>Narration:</strong>{' '}
              <input
                type="text"
                value={narrationValue}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, narration: e.target.value }))
                }}
                className="border-none bg-transparent focus:bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-blue-400 px-1 rounded w-[calc(100%-80px)]"
                placeholder="Enter narration"
              />
            </div>
          )}

          {!ipFinalBillDetails && (
          <>
            {/* Signature Section - PDF Style */}
            <div className="border-t border-b border-black py-1.5 mt-10 mb-5 flex justify-between text-[10px]">
              <div>Patient / Attendants Sign</div>
              <div>for EPIONE PAIN CARE PRIVATE LIMITED - JUBILEEHILLS, HYDERABAD (CENTER)</div>
            </div>

          {/* Footer - PDF Style */}
          <div className="border-t border-black pt-2.5 text-center text-[9px] leading-tight mt-5">
            <p className="mb-0.5"><strong>Important:</strong> Please collect your receipt / invoice before leaving the counter</p>
            <p className="mb-2"><strong>Note:</strong> Epione Pain care Pvt. Ltd. will not be responsible for any deposit for which receipts are not taken</p>
            
            <div className="border-t border-gray-300 pt-2 mt-2">
              <p className="mb-0.5"><strong>Our Corporate Office Address</strong></p>
              <p className="font-semibold mb-0.5">Epione Pain Care Private Limited</p>
              <p className="mb-0.5">CIN No: U85300TG2019PTC136629</p>
              <p>Plot No. 1630, 2nd Floor, Road No 36, Jubilee Hills, Hyderabad – 500033</p>
            </div>
            </div>
          </>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}

export default ReceiptEditor
